import { useMemo, type ComponentProps } from "react";

import { NativeSelect } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const fallbackZones = ["Europe/Moscow", "Europe/Kaliningrad", "Europe/Samara", "Asia/Yekaterinburg", "UTC"];

function zones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return fallbackZones;
  }
}

/** Offset like "UTC+03:00" of a zone right now. */
function offset(zone: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return part === "GMT" ? "UTC+00:00" : (part?.replace("GMT", "UTC") ?? "");
  } catch {
    return "";
  }
}

/** IANA time zone picker; keeps an unknown current value selectable. */
export function TimezoneSelect({
  value,
  className,
  ...props
}: ComponentProps<typeof NativeSelect> & { value: string }) {
  const options = useMemo(() => {
    const list = zones();
    if (value && !list.includes(value)) list.unshift(value);
    if (!list.includes("UTC")) list.push("UTC");
    return list.map((z) => ({ zone: z, label: `${z.replaceAll("_", " ")} (${offset(z)})` }));
  }, [value]);

  return (
    <NativeSelect value={value} className={cn("h-10 w-full", className)} {...props}>
      {options.map((o) => (
        <option key={o.zone} value={o.zone}>
          {o.label}
        </option>
      ))}
    </NativeSelect>
  );
}

/** The browser's time zone, used as the default in forms. */
// eslint-disable-next-line react-refresh/only-export-components
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Moscow";
  } catch {
    return "Europe/Moscow";
  }
}
